import { Box, Container, Grid, Stack, Text, Group } from '@mantine/core';
import { FadeIn } from '../UI/FadeIn';
import { heroFontFamily } from '../../theme';

// On mobile the columns stack, so center the icon, quote, and attribution;
// at the `md` breakpoint (where the columns sit side by side) revert to left.
const testimonialStyles = `
  .testimonial-col {
    text-align: center;
  }
  .testimonial-quote-icon {
    display: block;
    margin-inline: auto;
  }
  .testimonial-attribution {
    justify-content: center;
  }
  @media (min-width: 62em) {
    .testimonial-col { text-align: left; }
    .testimonial-quote-icon { margin-inline: 0; }
    .testimonial-attribution { justify-content: flex-start; }
  }
`;

const TESTIMONIALS = [
  {
    quote: (
      <>
        <b>I don't have to think about what to cook</b> that's nutritious, meets
        my dietary needs, and doesn't take hours in the kitchen. It has saved me
        so much time and money, plus I can already feel the difference
        health-wise.
      </>
    ),
    name: 'Jennifer C.'
  },
  {
    quote: (
      <>
        I've tried a lot of meal planning apps, and most had shortcomings. I have{' '}
        <b>dietary limits that were next to impossible to work in until now</b>.
        And the free trial let me try every feature before committing, with no
        credit card. An absolute win in my book.
      </>
    ),
    name: 'Sheila S.'
  },
  {
    quote: (
      <>
        <b>The nutritional breakdown is exactly what I need.</b> I'm on a GLP-1
        and protein is a priority. I refuse to track my food or calculate
        macros. I feel that would harm my relationship with food.
      </>
    ),
    name: 'Stacey S.'
  },
  {
    quote: (
      <>
        The recipes have been a <b>HUGE hit with my very, very picky family</b>.
        I've frankly been astonished at how well they've gone over, based on
        how easy they are to make.
      </>
    ),
    name: 'Jessica B.'
  }
];

interface SocialProofProps {
  // Section background. Override per page so it alternates cleanly with the
  // sections above and below it rather than blending into an adjacent band.
  background?: string;
  // Bracket the section with hairline rules top and bottom. Use when the
  // section shares a background with its neighbors and needs separation
  // without a contrasting fill.
  withDivider?: boolean;
  // Divider line color. Match it to the section background so the hairline
  // doesn't read as a foreign gray line on a tinted band.
  dividerColor?: string;
}

export const SocialProof = ({
  background = 'linear-gradient(135deg, rgba(255, 248, 240, 1) 0%, rgba(255, 250, 245, 1) 100%)',
  withDivider = false,
  dividerColor = 'var(--mantine-color-gray-2)'
}: SocialProofProps) => {
  return (
    <Box
      py={{ base: 40, md: 60 }}
      style={{
        background,
        ...(withDivider
          ? {
              borderTop: `1px solid ${dividerColor}`,
              borderBottom: `1px solid ${dividerColor}`
            }
          : {})
      }}
    >
      <style>{testimonialStyles}</style>
      <Container size="lg">
        {/* gutter must stay ≤ 2× container padding (32px) at every breakpoint or
            the Grid's negative margins poke past the viewport and cause
            page-wide horizontal scroll. `lg` is wider than a tablet viewport,
            so unlike the old `md` container there's no slack to absorb them.
            `stretch` gives every column the row's full height so the shorter
            quotes still line their attributions up along the bottom.
            Four testimonials sit 2×2: half-width columns keep the quotes
            from running to two-word lines, which three-up already risked. */}
        <Grid gutter={32} align="stretch">
          {TESTIMONIALS.map((testimonial, index) => (
            <Grid.Col key={index} span={{ base: 12, md: 6 }}>
              <FadeIn delay={index * 0.1} style={{ height: '100%' }}>
                <Stack gap="lg" h="100%" className="testimonial-col">
                  {/* A serif opening quote, not IconQuote: the Tabler glyph is
                      a closing-quote pair ("99") and reads backwards at the
                      start of a quote. The height crops the em box's empty
                      descender space so the Stack gap measures from the ink. */}
                  <Text
                    component="span"
                    aria-hidden="true"
                    className="testimonial-quote-icon"
                    style={{
                      fontFamily: heroFontFamily,
                      fontSize: 88,
                      lineHeight: 1,
                      height: 44,
                      overflow: 'hidden',
                      color: 'var(--mantine-color-orange-3)',
                      flexShrink: 0,
                      userSelect: 'none'
                    }}
                  >
                    &ldquo;
                  </Text>
                  <Text
                    style={{
                      flex: 1,
                      fontFamily: heroFontFamily,
                      fontSize: 'clamp(1.02rem, 1.8vw, 1.18rem)',
                      lineHeight: 1.65,
                      color: 'var(--mantine-color-dark-6)'
                    }}
                  >
                    {testimonial.quote}
                  </Text>
                  <Group gap="sm" wrap="nowrap" align="center" className="testimonial-attribution">
                    <Box
                      style={{
                        width: 16,
                        height: 2,
                        borderRadius: 2,
                        flexShrink: 0,
                        background: 'var(--mantine-color-orange-5)'
                      }}
                    />
                    <Text size="sm" fw={600} c="dimmed">
                      {testimonial.name}
                    </Text>
                  </Group>
                </Stack>
              </FadeIn>
            </Grid.Col>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};
